import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { FlightSearchAirport, FlightSearchPayload } from '../../shared/flight-search-bar/flight-search-bar.component';
import { FlightRouteContent, ROUTE_SEARCH_ID, RouteAirport, loadFlightRoutes } from '../flight-route.model';

function toSearchAirport(airport: RouteAirport): FlightSearchAirport {
  return { name: airport.airport, city: airport.city, country: airport.country, iata: airport.iata };
}

/**
 * Shell for a flight route landing page (e.g. /flights/nairobi-to-mombasa):
 * header, footer and the search widget prefilled with the route, which all live
 * in AppModule, between the hero and body (standalone components). The route
 * copy is resolved as `data.flightRoute`; SeoService sets the title/meta/JSON-LD,
 * and scripts/seo-prerender.mjs writes the same copy as static HTML.
 */
@Component({
  selector: 'app-flight-route',
  templateUrl: './flight-route.component.html',
  styleUrls: ['./flight-route.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FlightRouteComponent {
  private readonly activatedRoute = inject(ActivatedRoute);

  readonly searchId = ROUTE_SEARCH_ID;

  readonly route = toSignal(
    this.activatedRoute.data.pipe(map(data => data['flightRoute'] as FlightRouteContent | undefined)),
    { initialValue: this.activatedRoute.snapshot.data['flightRoute'] as FlightRouteContent | undefined }
  );

  /** The resolver has already loaded the routes, so this resolves straight away. */
  private readonly allRoutes = toSignal(from(loadFlightRoutes()), { initialValue: [] as readonly FlightRouteContent[] });

  readonly related = computed<FlightRouteContent[]>(() => {
    const all = this.allRoutes();
    return (this.route()?.related ?? [])
      .map(slug => all.find(route => route.slug === slug))
      .filter((route): route is FlightRouteContent => !!route);
  });

  /** Prefills origin and destination only; the traveller still picks the date. */
  readonly prefill = computed<Partial<FlightSearchPayload> | null>(() => {
    const route = this.route();
    if (!route) {
      return null;
    }
    return {
      from_data: toSearchAirport(route.from),
      destination_data: toSearchAirport(route.to),
      trip_type: 'oneway',
      returning: false,
      adult_count: 1,
      child_count: 0,
      infant_count: 0,
      travel_class: 'economy'
    };
  });
}
